import { useQueryClient } from "@tanstack/react-query";
import { App, Form, InputNumber, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomSwitch, CustomTypography } from "sipli-fleet-core/components";
import CustomInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import {
  useMutationSaveTachographCompanySettings,
  useQueryGetTachographCompanySettings,
} from "src/queries/tachographCompany.queries";

import { QueryNames } from "src/queries/queries.constant";
import type { TachographCompanySettingsPayload } from "src/services/tachographCompanyService";

import TachographActivationModal from "./TachographActivationModal";

interface TachographSettingsTabProps {
  clientId?: string;
}

interface TachographSettingsFormValues {
  tachographExternalId: string;
  tachographVehicleDownloadFrequency: number | null;
  tachographDriverDownloadFrequency: number | null;
}

const DEFAULT_TACHOGRAPH_VALUES: TachographSettingsFormValues = {
  tachographExternalId: "",
  tachographVehicleDownloadFrequency: null,
  tachographDriverDownloadFrequency: null,
};

const getStringValue = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === "string") return value;
  }

  return "";
};

const positiveIntegerRule = (message: string) => ({
  validator: (_: unknown, value: unknown) => {
    const parsedValue = Number(value);

    if (Number.isInteger(parsedValue) && parsedValue > 0) {
      return Promise.resolve();
    }

    return Promise.reject(new Error(message));
  },
});

const getTachographFormValues = (
  setting?: {
    externalId?: string | null;
    vehicleSchedule?: number | null;
    driverSchedule?: number | null;
  } | null
) => {
  return {
    tachographExternalId: getStringValue(setting?.externalId),
    tachographVehicleDownloadFrequency: setting?.vehicleSchedule ?? null,
    tachographDriverDownloadFrequency: setting?.driverSchedule ?? null,
  };
};

const TachographSettingsTab = ({ clientId }: TachographSettingsTabProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const submitting = useRef(false);
  const currentClientId = useRef(clientId);
  currentClientId.current = clientId;
  const [pendingRequest, setPendingRequest] = useState<(TachographCompanySettingsPayload & { activation: boolean }) | null>(null);
  const [draftEnabled, setDraftEnabled] = useState<boolean | null>(null);
  const [modalMode, setModalMode] = useState<"activated" | "deactivate" | null>(null);
  const [form] = Form.useForm<TachographSettingsFormValues>();
  const { data, isLoading, isFetching, isError } = useQueryGetTachographCompanySettings(clientId, {
    enabled: Boolean(clientId),
    refetchInterval: pendingRequest ? 2000 : false,
  });
  const { mutateAsync: saveTachographCompanySettings, isPending } =
    useMutationSaveTachographCompanySettings();
  const tachographSettings = data?.data?.response;
  const serviceEnabled = tachographSettings?.enabled === true;
  const formEnabled = draftEnabled ?? serviceEnabled;
  const busy = isPending || pendingRequest !== null;

  useEffect(() => {
    setPendingRequest(null);
    setDraftEnabled(null);
    setModalMode(null);
  }, [clientId]);

  useEffect(() => setDraftEnabled(null), [tachographSettings?.enabled]);

  useEffect(() => {
    if (!pendingRequest || !tachographSettings || isError) return;
    if (serviceEnabled !== pendingRequest.enabled) return;

    if (pendingRequest.enabled) {
      if (!tachographSettings.externalId) return;
      if (
        tachographSettings.vehicleSchedule !== pendingRequest.vehicleSchedule ||
        tachographSettings.driverSchedule !== pendingRequest.driverSchedule
      ) {
        return;
      }
    }

    setPendingRequest(null);
    if (!pendingRequest.enabled) {
      void queryClient.invalidateQueries({ queryKey: QueryNames.tachographCardList });
      void queryClient.invalidateQueries({ queryKey: QueryNames.tachographCardDetail });
      message.success(t("tachograph_service_disabled"));
    } else if (!pendingRequest.activation) {
      message.success(t("tachograph_setting_updated_successfully"));
    }
  }, [pendingRequest, tachographSettings, isError, serviceEnabled, queryClient, message, t]);

  useEffect(() => {
    if (isLoading || !tachographSettings) {
      form.setFieldsValue(DEFAULT_TACHOGRAPH_VALUES);
      return;
    }

    form.setFieldsValue(getTachographFormValues(tachographSettings));
  }, [form, isLoading, tachographSettings]);

  const handleSave = async (enabled = formEnabled, validateForm = true) => {
    if (!clientId || submitting.current || busy) return;
    submitting.current = true;

    try {
      let values = getTachographFormValues(tachographSettings);
      if (enabled && validateForm) {
        try {
          values = await form.validateFields([
            "tachographVehicleDownloadFrequency",
            "tachographDriverDownloadFrequency",
          ]);
        } catch {
          return;
        }
      }
      const payload: TachographCompanySettingsPayload = {
        customerId: Number(clientId),
        enabled,
        vehicleSchedule: values.tachographVehicleDownloadFrequency,
        driverSchedule: values.tachographDriverDownloadFrequency,
      };
      await saveTachographCompanySettings(payload);
      if (currentClientId.current !== clientId) return;
      // A 202 accepts the operation; GET confirms completion and supplies the ID.
      setModalMode(enabled && !serviceEnabled ? "activated" : null);
      setDraftEnabled(null);
      setPendingRequest({ ...payload, activation: enabled && !serviceEnabled });
    } catch {
      if (currentClientId.current === clientId) {
        setDraftEnabled(null);
        message.error(t("failed_to_update_tachograph_setting"));
      }
    } finally {
      submitting.current = false;
    }
  };

  const handleSwitchChange = (enabled: boolean) => {
    form.setFields([
      { name: "tachographVehicleDownloadFrequency", errors: [], warnings: [] },
      { name: "tachographDriverDownloadFrequency", errors: [], warnings: [] },
    ]);
    if (enabled) {
      setDraftEnabled(true);
      void handleSave(true, false);
    } else {
      setDraftEnabled(false);
      setModalMode("deactivate");
    }
  };

  return (
    <div className="min-h-[214px] overflow-hidden rounded-[8px] border border-solid border-gray3 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-[16px] border-b border-gray3 px-[24px] py-[16px]">
        <CustomTypography.CustomText className="!text-[19.93px] !leading-[28px] !font-bold !text-black">
          {t("tachograph")}
        </CustomTypography.CustomText>
        <div className="flex flex-wrap items-center gap-[16px]">
          <div className="flex items-center gap-[12px]">
            <span id="customer-tachograph-activation" className="text-[14px] leading-[18px] font-semibold text-black">
              {t(formEnabled ? "tachograph_disable_service" : "tachograph_enable_service")}
            </span>
            <CustomSwitch
              aria-labelledby="customer-tachograph-activation"
              checked={formEnabled}
              loading={busy}
              disabled={!clientId || isLoading || isFetching || isError || busy}
              onChange={handleSwitchChange}
            />
          </div>
          <div className="border-l border-gray5 pl-[32px]">
            <CustomButton
              type="primary"
              label={t("save")}
              loading={isPending}
              disabled={!clientId || isLoading || isError || busy || (!formEnabled && !serviceEnabled)}
              onClick={() => void handleSave()}
              className="!h-[32px] !px-[16px] !text-[14px] !font-semibold !text-gray2"
            />
          </div>
        </div>
      </div>

      <div className="px-[24px] py-[32px]">
        {pendingRequest && (
          <p role="status" className="mb-[16px] text-gray8">{t("tachograph_processing")}</p>
        )}
        <CustomTypography.CustomText className="!block border-b border-gray3 pb-[8px] !text-[17.72px] !leading-[24px] !font-semibold !text-black">
          {t("settings")}
        </CustomTypography.CustomText>
        <Spin spinning={isLoading || isFetching}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            preserve
            disabled={!formEnabled || isLoading || isError || busy}
            className="!mt-[24px] grid grid-cols-1 gap-x-[24px] gap-y-[24px] md:grid-cols-2 [&_.ant-form-item]:!mb-0 [&_.ant-form-item-label>label]:!text-[14px] [&_.ant-form-item-label>label]:!leading-[18px] [&_.ant-form-item-label>label]:!text-black"
          >
            <Form.Item
              name="tachographExternalId"
              label={t("customer_form_external_tacho_id")}
            >
              <CustomInput
                readOnly
                className="common-input !mt-0 !h-[32px] !w-full"
              />
            </Form.Item>

            <Form.Item
              name="tachographVehicleDownloadFrequency"
              label={t("ddd_vehicle_download_frequency_days")}
              rules={[
                {
                  required: true,
                  message: t("customer_form_tacho_vehicle_frequency_required"),
                },
                positiveIntegerRule(t("customer_form_positive_integer_required")),
              ]}
            >
              <InputNumber min={1} className="common-input !mt-0 !h-[32px] !w-full" />
            </Form.Item>

            <Form.Item
              name="tachographDriverDownloadFrequency"
              label={t("ddd_driver_download_frequency_days")}
              rules={[
                {
                  required: true,
                  message: t("customer_form_tacho_driver_frequency_required"),
                },
                positiveIntegerRule(t("customer_form_positive_integer_required")),
              ]}
            >
              <InputNumber min={1} className="common-input !mt-0 !h-[32px] !w-full" />
            </Form.Item>
          </Form>
        </Spin>

      </div>
      <TachographActivationModal
        mode={modalMode}
        loading={isPending}
        onClose={() => {
          if (isPending) return;
          if (modalMode === "deactivate") setDraftEnabled(null);
          setModalMode(null);
        }}
        onConfirm={() => void handleSave(false)}
      />
    </div>
  );
};

export default TachographSettingsTab;
